import React, { useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../../app/hooks'
import { selectCurrentAssetHolderId } from './editAssetHolderSlice'
import { useUpdateEmailMutation } from '../../../api-services/asset-holdings/assetHolderApi'
import { type UpdateAssetHolderEmailAddressPayload } from '../../../api-schema-types/asset-holdings/AssetHolderPayloadDto'
import { setNotification } from '../../components/ui-widgets/uiWidgetsSlice'
import { RtkQueryHelper } from '../../../api-services/shared/RtkQueryHelper'
import Button from '@mui/material/Button'
import LockResetIcon from '@mui/icons-material/LockReset'
import { Dialog, DialogContent, DialogTitle } from '@mui/material'
import { Formik } from 'formik'
import Grid from '@mui/material/Grid'
import ErrorComponent from '../../components/ui-widgets/ErrorComponent'
import TextField from '@mui/material/TextField'
import Box from '@mui/material/Box'
import ButtonLoadingComponent from '../../../views/components/shared/ButtonLoadingComponent'

const UpdateEmailAddressComponent: React.FC = () => {
  const dispatch = useAppDispatch()
  const currentAssetHolderId = useAppSelector<number | null>(selectCurrentAssetHolderId)

  const [openDialog, toggleDialog] = useState<boolean>(false)
  const [apiUpdateEmail, { isLoading }] = useUpdateEmailMutation()

  const defaultForm: UpdateAssetHolderEmailAddressPayload = {
    assetHolderId: currentAssetHolderId ?? 0,
    emailAddress: ''
  }

  const handleUpdateEmail = (payload: UpdateAssetHolderEmailAddressPayload): void => {
    apiUpdateEmail(payload)
      .unwrap()
      .then(() => {
        dispatch(setNotification({ open: true, severity: 'success', message: 'Email address has been updated.' }))
        toggleDialog(false)
      })
      .catch((err) => {
        dispatch(setNotification({ open: true, severity: 'error', message: RtkQueryHelper.mapResponseError(err) }))
      })
  }

  return (
    <>
      <Button
        onClick={() => { toggleDialog(true) }}
        startIcon={<LockResetIcon />}
        variant={'outlined'}
      >
        Change Email Address
      </Button>

      <Dialog
        open={openDialog}
        maxWidth={'sm'}
        fullWidth={true}
        disableEscapeKeyDown={true}
        onClose={() => { toggleDialog(false) }}
        sx={{ p: 2 }}
      >
        <DialogTitle>Edit Email Address</DialogTitle>

        <DialogContent>
          <Formik
            initialValues={defaultForm}
            onSubmit={(values) => {
              handleUpdateEmail(values)
            }}
          >
            {formikProps => (
              <form onSubmit={formikProps.handleSubmit}>
                <Grid container spacing={3}>
                  {!(formikProps.isValid) && (
                    <Grid item md={12} sx={{ mt: 3 }}>
                      <ErrorComponent title={'Form validation error'} message={Object.values(formikProps.errors).join(', ')}/>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <TextField
                      label={'Email Address'}
                      type={'text'}
                      variant="outlined"
                      fullWidth={true}
                      placeholder={'Email Address'}
                      {...formikProps.getFieldProps('emailAddress')}
                    />
                  </Grid>

                  <Grid item md={12}>
                    <Box display={'flex'} justifyContent={'space-between'}>

                      <Button
                        type={'submit'}
                        disabled={isLoading}
                        variant={'outlined'}
                      >
                        Save Changes
                        {isLoading && <ButtonLoadingComponent/>}
                      </Button>
                      <Button
                        variant={'outlined'}
                        type="button"
                        disabled={isLoading}
                        sx={{ ml: 2 }}
                        onClick={() => { toggleDialog(false) }}
                      >
                        Cancel
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default UpdateEmailAddressComponent
